/**
 * Look at the web component that is the parent of one we want to dynamically create, and copy its prefix to determine
 * the tag name of the new component.
 *
 * @param parentEl      HTMLElement   DOM reference of actual existing parent element
 * @param componentName string        New proposed tag name for the new child component to be created, always beginning with 'ds-'
 *
 * @return              string        The actual tag name to use, possibly with a prefix copied from the parent, eg. 'ipx-ds-button'
 */
import { dsConsole } from './console';

export const calculateChildTagName = (
	parentEl: HTMLElement,
	componentName: string
): string => {
	try {
		const parentTagName = parentEl.tagName.toLowerCase();
		const parentTagPrefix = parentTagName.split('-')[0];
		// Default prefix case
		if (parentTagPrefix === 'ds') return componentName;

		// If parent tag has a different prefix than just 'ds-' then use it (eg. 'ipx-ds-')
		return `${parentTagPrefix}-${componentName}`;
	} catch (error) {
		dsConsole.error(
			`calculateChildTagName(): Unable to determine tag name for new child component "${componentName}" in "${parentEl.tagName.toLowerCase()}`,
			error
		);

		return componentName;
	}
};

/**
 * Like calculateChildTagName() but returns a child component of the parent, most likely present due to using a slot
 */
export const getChildComponent = <Type extends HTMLElement>(
	parentEl: HTMLElement,
	componentName: string
): Type => {
	try {
		return parentEl.querySelector<Type>(
			calculateChildTagName(parentEl, componentName)
		);
	} catch (error) {
		dsConsole.error(
			`getChildComponent(): Unable to find child component "${componentName}" in "${parentEl.tagName.toLowerCase()}`,
			error
		);

		return null;
	}
};

/**
 * Like calculateChildTagName() but returns an array of child components, most likely present due to using a slot
 */
export const getChildComponents = <Type extends HTMLElement>(
	parentEl: HTMLElement,
	componentName: string
): Type[] => {
	try {
		return Array.from(
			parentEl.querySelectorAll<Type>(
				calculateChildTagName(parentEl, componentName)
			)
		);
	} catch (error) {
		dsConsole.error(
			`getChildComponents(): Unable to find child components "${componentName}" in "${parentEl.tagName.toLowerCase()}`,
			error
		);

		return [];
	}
};
